import React, { useState, useEffect, memo } from 'react';
import { useThrottle } from '@/hooks/usePerformanceOptimization';
import { useReducedMotion } from './AccessibilityUtils';

interface ProgressIndicatorProps {
  className?: string;
  showPercentage?: boolean;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = memo(({ 
  className = '',
  showPercentage = false 
}) => {
  // Performance optimized
  const [scrollProgress, setScrollProgress] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  // Throttle scroll handler for better performance
  const throttledScrollHandler = useThrottle(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    
    setScrollProgress(Math.min(Math.max(progress, 0), 100));
  }, 16); // ~60fps

  useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    throttledScrollHandler(); // Initial calculation
    
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [throttledScrollHandler]);

  return (
    <>
      {/* Fixed Progress Bar */}
      <div 
        className={`fixed top-0 left-0 right-0 z-50 ${className}`}
        role="progressbar"
        aria-label="Reading progress"
        aria-valuenow={Math.round(scrollProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div 
          className={`h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 shadow-lg ${
            prefersReducedMotion ? '' : 'transition-all duration-300 ease-out'
          }`}
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Optional Percentage Display */}
      {showPercentage && scrollProgress > 5 && (
        <div 
          className="fixed top-4 right-4 z-40 bg-black/50 backdrop-blur-xl text-white text-sm px-3 py-1 rounded-full border border-white/20"
          aria-live="polite"
          aria-label={`Reading progress: ${Math.round(scrollProgress)} percent`}
        >
          {Math.round(scrollProgress)}%
        </div>
      )}
    </>
  );
});

ProgressIndicator.displayName = 'ProgressIndicator';

// Circular progress indicator variant

export const CircularProgressIndicator: React.FC<{ 
  size?: number; 
  strokeWidth?: number;
  className?: string;
}> = memo(({ 
  size = 48, 
  strokeWidth = 3,
  className = '' 
}) => {
  usePerformanceMonitor('CircularProgressIndicator');
  const [scrollProgress, setScrollProgress] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  const throttledScrollHandler = useThrottle(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    
    setScrollProgress(Math.min(Math.max(progress, 0), 100));
  }, 16);

  useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    throttledScrollHandler();
    
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [throttledScrollHandler]);

  if (scrollProgress < 5) return null;

  return (
    <div 
      className={`fixed bottom-8 left-8 z-40 ${className}`}
      role="progressbar"
      aria-label="Reading progress"
      aria-valuenow={Math.round(scrollProgress)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="relative">
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
          aria-hidden="true"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#progressGradient)"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className={prefersReducedMotion ? '' : 'transition-all duration-300 ease-out'}
          />
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="50%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
          </defs>
        </svg>
        
        {/* Percentage text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className="text-white text-xs font-medium"
            aria-label={`${Math.round(scrollProgress)} percent complete`}
          >
            {Math.round(scrollProgress)}%
          </span>
        </div>
      </div>
    </div>
  );
});

CircularProgressIndicator.displayName = 'CircularProgressIndicator';

export default ProgressIndicator;